import * as React from 'react'
import { useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import i18n from 'live/services/i18n'

export type CardContainerProps = {
    title: string | React.ReactNode
    subtitle: React.ReactNode
    collapsed: boolean
    stickyControls: boolean
    children?: React.ReactNode
    className?: string
    toolTip?: (opt: { collapsed: boolean; toggleCollapsed: () => void }) => React.ReactNode
    renderControls?: (opt: { collapsed: boolean; toggleCollapsed: () => void }) => React.ReactNode
}

const useCollapsed = (collapsedProp: boolean): [boolean, React.Dispatch<React.SetStateAction<boolean>>] => {
    const [collapsed, setCollapsed] = useState(!!collapsedProp)

    return [collapsed, () => setCollapsed(!collapsed)]
}

const CardContainer: React.FunctionComponent<CardContainerProps> = ({
    stickyControls = false,
    collapsed: collapsedProp = false,
    className = '',
    toolTip,
    renderControls,
    children,
    title,
    subtitle
}: CardContainerProps) => {
    const [collapsed, toggleCollapsed] = useCollapsed(collapsedProp)

    useEffect(() => {
        if (collapsedProp !== collapsed) {
            toggleCollapsed(collapsedProp)
        }
    }, [collapsedProp])

    return (
        <BorderContainer className={className} stickyControls={stickyControls}>
            <header>
                <Title collapsed={collapsed}>{title}</Title>
                {toolTip && (
                    <CardContainerControlsDefault>
                        {typeof toolTip === 'function' &&
                            toolTip({
                                collapsed,
                                toggleCollapsed
                            })}
                    </CardContainerControlsDefault>
                )}
                {subtitle}
                <CardContainerControls>
                    {typeof renderControls === 'function' &&
                        renderControls({
                            collapsed,
                            toggleCollapsed
                        })}
                </CardContainerControls>
            </header>
            {collapsed ? null : children}
        </BorderContainer>
    )
}
export default CardContainer

export const CollapseButton = ({ collapsed, toggleCollapsed }: { collapsed: boolean; toggleCollapsed: () => void }) => (
    <ControlButton onClick={toggleCollapsed}>
        <span>{collapsed ? i18n('SHOW') : i18n('HIDE')}</span>
        <i className={collapsed ? 'far fa-plus-square' : 'far fa-minus-square'} />
    </ControlButton>
)

export const CardContainerControls = styled.div`
    display: flex;
    flex: 1;
    justify-content: flex-end;
`

export const CardContainerControlsDefault = styled.div`
    display: flex;
    justify-content: flex-start;
`

export const ControlButton: StyledComponent<'div', any, { disabled?: boolean }, never> = styled.div`
    cursor: pointer;
    font-family: ${({ theme }) => theme['font-semibold']};
    font-size: 10px;
    color: ${({ theme }) => theme['link-button']};
    display: flex;
    align-items: center;

    > span {
        margin-right: 4px;
    }

    > i {
        font-size: 16px;
    }
    ${props =>
        props.disabled &&
        css`
            pointer-events: none;
            cursor: default;
            opacity: 0.5;
        `}
`

export const BorderContainer = styled.div`
    padding: 16px 22px;
    box-shadow: 0 0 5px 0 rgba(54, 65, 89, 0.2);
    margin-bottom: 22px;
    background-color: var(--gray-0);

    border-radius: 3px;
    border: 1px solid var(--gray-300);
    display: block;

    ${props =>
        props.disabled &&
        css`
            opacity: 0.5;
        `}

    > header {
        align-items: center;
        display: flex;
        justify-content: space-between;
        flex: 1;
        ${({ stickyControls }) =>
            stickyControls
                ? css`
                      position: sticky;
                      top: 0;
                  `
                : ''}
    }
    ${({ stickyControls }) =>
        stickyControls
            ? css`
                  position: relative;
              `
            : ''}
`

export const Title = styled.div`
    font-family: ${({ theme }) => theme['font-semibold']};
    color: ${({ theme }) => theme['main-tipography']};
    font-size: 14px;
    margin-bottom: 2px;
    align-items: center;
    display: flex;
`
